import { useEffect, useRef } from 'react';

import { TaskType, TaskView } from '@ndvr/customer-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { titleCase } from 'utils';

import { useTaskViewState } from '../task-state';
import { TaskTypeIcon } from './TaskTypeIcon';

interface TaskTypeFilterProps {
    tasks: TaskView[];
    searchTerm: string;
    onSearchChange: (value: string) => void;
    focusSearchTrigger: number;
}

export const TaskTypeFilter = ({ tasks, searchTerm, onSearchChange, focusSearchTrigger }: TaskTypeFilterProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get unique task types from the provided task data
    const availableTaskTypes = (() => {
        if (!tasks.length) return [];

        const uniqueTaskTypes = Array.from(new Set(tasks.map((task) => task.type).filter((type) => type != null)));

        return uniqueTaskTypes
            .map((type) => ({
                type,
                label: titleCase(type),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    })();

    // Filter task types based on search term
    const filteredTaskTypes = availableTaskTypes.filter((taskType) =>
        taskType.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleTaskTypeToggle = (taskType: TaskType) => {
        const currentSelected = taskViewState.selectedTaskTypes;
        const isSelected = currentSelected.includes(taskType);

        const newSelected = isSelected
            ? currentSelected.filter((type) => type !== taskType)
            : [...currentSelected, taskType];

        setTaskViewState({ selectedTaskTypes: newSelected });
    };

    // Focus search input when requested
    useEffect(() => {
        if (focusSearchTrigger > 0) {
            searchInputRef.current?.focus();
        }
    }, [focusSearchTrigger]);

    return (
        <div className="flex h-full flex-col space-y-3">
            <div className="p-4 pt-2">
                <Input
                    ref={searchInputRef}
                    placeholder="Search task types..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                    {filteredTaskTypes.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">No task types found</div>
                    ) : (
                        filteredTaskTypes.map((taskType) => {
                            const isSelected = taskViewState.selectedTaskTypes.includes(taskType.type);
                            return (
                                <div
                                    key={taskType.type}
                                    className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                                    onClick={() => handleTaskTypeToggle(taskType.type)}
                                >
                                    <Checkbox checked={isSelected} />
                                    <div className="flex items-center">
                                        <TaskTypeIcon type={taskType.type} />
                                        <span className="ml-2">{taskType.label}</span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};
